import { useState, useRef, useEffect, useCallback } from 'react';
import { RTVIClient, RTVIEvent } from '@pipecat-ai/client-js';
import { WebSocketTransport } from '@pipecat-ai/websocket-transport';
import { Mic, MicOff, X, Clock, Play, Presentation, Code, FileText, Sparkles, Star } from 'lucide-react';

const LiveCoursePreview = ({ 
  candidateName = "Student",
  candidateEmail = "",
  candidateId = "",
  duration = 5,
  onPreviewEnd = () => {},
  onExit = null
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMicEnabled, setIsMicEnabled] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Ready to start preview');
  const [timeRemaining, setTimeRemaining] = useState(duration * 60);
  const [transcript, setTranscript] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('male-1');
  const [currentTeacherText, setCurrentTeacherText] = useState('');
  const [activeView, setActiveView] = useState('whiteboard');
  const [whiteboardLines, setWhiteboardLines] = useState([]);
  const [codeContent, setCodeContent] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [lastMessageIndex, setLastMessageIndex] = useState(0);
  const [textQueue, setTextQueue] = useState([]);
  const [diagrams, setDiagrams] = useState([]);
  const [showTranscript, setShowTranscript] = useState(true);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [wouldEnroll, setWouldEnroll] = useState(false);
  
  const voiceOptions = [
    { value: 'female-1', label: 'Sarah - Breezy & Expressive (Female)', gender: 'female' },
    { value: 'female-2', label: 'Diana - Firm & Authoritative (Female)', gender: 'female' },
    { value: 'female-indian-1', label: 'Priya - Expressive (Indian Female)', gender: 'female' },
    { value: 'male-1', label: 'Alex - Upbeat & Energetic (Male)', gender: 'male' },
    { value: 'male-2', label: 'James - Calm & Professional (Male)', gender: 'male' },
    { value: 'male-indian-1', label: 'Arjun - Energetic (Indian Male)', gender: 'male' },
  ];
  
  const rtviClientRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioElementRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const whiteboardRef = useRef(null);
  const textTimeoutRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const textQueueRef = useRef(null);
  const isBotSpeakingRef = useRef(false);
  const botSpeakingCooldownRef = useRef(null);

  const addLog = useCallback((message) => {
    console.log(`[Preview] ${message}`);
  }, []);

  useEffect(() => {
    setTimeRemaining(duration * 60);
  }, [duration]);

  const getApiBaseUrl = useCallback(() => {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:8000`;
    }
    
    const backendUrl = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';
    return backendUrl.replace('/api', '');
  }, []);

  const setupAudioTrack = useCallback((track) => {
    addLog('Setting up audio track');
    if (audioElementRef.current) {
      const stream = new MediaStream([track]);
      audioElementRef.current.srcObject = stream;
      audioElementRef.current.play().catch(e => addLog(`Audio play failed: ${e}`));
    }
  }, [addLog]);

  const showTeacherText = useCallback((text) => {
    if (!text || !text.trim()) return;
    addLog(`Showing teacher text: ${text.substring(0, 50)}...`);
    setCurrentTeacherText(text);
    if (textTimeoutRef.current) clearTimeout(textTimeoutRef.current);
    const wordCount = text.split(' ').length;
    const displayTime = Math.max(3000, Math.min(wordCount * 200, 12000));
    textTimeoutRef.current = setTimeout(() => {
      setCurrentTeacherText('');
    }, displayTime);
  }, [addLog]);

  // Process content immediately — no queue delay
  const queueTeacherText = useCallback((text, isWhiteboard = false, isCode = false, isDiagram = false) => {
    if (isWhiteboard) {
      const lines = text.split('\n').filter(line => line.trim());
      setActiveView('whiteboard');
      lines.forEach((line, i) => {
        setTimeout(() => {
          setWhiteboardLines(prev => [...prev, line]);
        }, i * 300);
      });
    } else if (isCode) {
      setCodeContent(prev => prev + (prev ? '\n\n' : '') + text);
      setActiveView('code');
    } else if (isDiagram) {
      setDiagrams(prev => [...prev, { description: text, timestamp: Date.now() }]);
      setActiveView('whiteboard');
    } else if (text) {
      showTeacherText(text);
      setTranscript(prev => [...prev, { speaker: 'teacher', text, timestamp: new Date().toISOString() }]);
    }
  }, [showTeacherText, addLog]);

  const stopPreview = async (showFeedbackModal = true) => {
    try {
      // Clear intervals and timeouts first
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (botSpeakingCooldownRef.current) {
        clearTimeout(botSpeakingCooldownRef.current);
        botSpeakingCooldownRef.current = null;
      }
      
      // Disconnect RTVI client safely
      if (rtviClientRef.current) {
        try {
          await rtviClientRef.current.disconnect();
        } catch (disconnectError) {
          addLog(`Disconnect error (non-critical): ${disconnectError.message}`);
        }
        rtviClientRef.current = null;
      }
      
      // Update UI state
      setIsConnected(false);
      setIsMicEnabled(false);
      if (showFeedbackModal) {
        setShowFeedback(true);
      }
    } catch (error) {
      addLog(`Stop error: ${error.message}`);
      // Still update UI state even if there was an error
      setIsConnected(false);
      setIsMicEnabled(false);
      if (showFeedbackModal) {
        setShowFeedback(true);
      }
    }
  };

  const pollTranscripts = useCallback(async () => {
    if (!sessionId) return;
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/course-teaching/live-transcript/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        const messages = data.messages || [];
        
        // Check if bot has finished teaching
        if (data.completed || data.session_ended) {
          addLog('Bot finished teaching, showing feedback');
          await stopPreview();
          return;
        }
        
        if (messages.length > lastMessageIndex) {
          const newMessages = messages.slice(lastMessageIndex);
          setLastMessageIndex(messages.length);
          newMessages.forEach(msg => {
            if (msg.speaker === 'teacher' && msg.text) {
              // Regex for complete markers (with closing tags)
              const whiteboardRegex = /\[WHITEBOARD\]([\s\S]*?)\[\/WHITEBOARD\]/gi;
              const codeRegex = /\[CODE\]([\s\S]*?)\[\/CODE\]/gi;
              const diagramRegex = /\[DIAGRAM\]([\s\S]*?)\[\/DIAGRAM\]/gi;
              
              // Regex for unclosed markers (AI didn't close the tag)
              const unclosedWhiteboardRegex = /\[WHITEBOARD\]([\s\S]+)$/gi;
              const unclosedCodeRegex = /\[CODE\]([\s\S]+)$/gi;
              const unclosedDiagramRegex = /\[DIAGRAM\]([\s\S]+)$/gi;
              
              let match;
              let hasClosedMarkers = false;
              
              // Extract and queue whiteboard content (closed markers)
              while ((match = whiteboardRegex.exec(msg.text)) !== null) {
                const content = match[1].trim();
                if (content) {
                  queueTeacherText(content, true, false, false);
                  hasClosedMarkers = true;
                }
              }
              
              // Extract and queue code content (closed markers)
              while ((match = codeRegex.exec(msg.text)) !== null) {
                const code = match[1].trim();
                if (code) {
                  queueTeacherText(code, false, true, false);
                  hasClosedMarkers = true;
                }
              }
              
              // Extract and queue diagram descriptions (closed markers)
              while ((match = diagramRegex.exec(msg.text)) !== null) {
                const diagramDesc = match[1].trim();
                if (diagramDesc) {
                  queueTeacherText(diagramDesc, false, false, true);
                  hasClosedMarkers = true;
                }
              }
              
              // If no closed markers found, check for unclosed markers
              if (!hasClosedMarkers) {
                // Check for unclosed whiteboard
                const unclosedWB = unclosedWhiteboardRegex.exec(msg.text);
                if (unclosedWB && unclosedWB[1].trim()) {
                  queueTeacherText(unclosedWB[1].trim(), true, false, false);
                }
                
                // Check for unclosed code
                const unclosedCode = unclosedCodeRegex.exec(msg.text);
                if (unclosedCode && unclosedCode[1].trim()) {
                  queueTeacherText(unclosedCode[1].trim(), false, true, false);
                }
                
                // Check for unclosed diagram
                const unclosedDiagram = unclosedDiagramRegex.exec(msg.text);
                if (unclosedDiagram && unclosedDiagram[1].trim()) {
                  queueTeacherText(unclosedDiagram[1].trim(), false, false, true);
                }
              }
              
              // Queue display text (without markers and internal AI thinking)
              let displayText = msg.text
                .replace(/\[WHITEBOARD\][\s\S]*?\[\/WHITEBOARD\]/gi, '')
                .replace(/\[CODE\][\s\S]*?\[\/CODE\]/gi, '')
                .replace(/\[DIAGRAM\][\s\S]*?\[\/DIAGRAM\]/gi, '')
                // Also remove unclosed markers
                .replace(/\[WHITEBOARD\][\s\S]*$/gi, '')
                .replace(/\[CODE\][\s\S]*$/gi, '')
                .replace(/\[DIAGRAM\][\s\S]*$/gi, '')
                .replace(/\[WHITEBOARD\]/gi, '').replace(/\[\/WHITEBOARD\]/gi, '')
                .replace(/\[CODE\]/gi, '').replace(/\[\/CODE\]/gi, '')
                .replace(/\[DIAGRAM\]/gi, '').replace(/\[\/DIAGRAM\]/gi, '')
                // Remove AI internal thinking/status markers like **Initiating Instruction**
                .replace(/\*\*[^*]+\*\*/g, '')
                // Remove any remaining asterisks used for emphasis
                .replace(/\*+/g, '')
                // Remove internal AI prompts and instructions
                .replace(/Hello [^!]+! Welcome to [^.]+\./gi, '')
                .replace(/Let me write our topics on the whiteboard:/gi, '')
                .replace(/I've greeted you[^.]+\./gi, '')
                .replace(/I am beginning the introduction[^.]+\./gi, '')
                .replace(/I've initiated[^.]+\./gi, '')
                .replace(/Writing on whiteboard:/gi, '')
                .replace(/Let me draw a diagram:/gi, '')
                .replace(/Let me show you/gi, '')
                .trim();
              
              // Skip if text is mostly internal AI status, very short, or contains meta-instructions
              const skipPhrases = [
                'module. i\'ve greeted',
                'i\'ve listed the module',
                'i am beginning',
                'commencing core concepts',
                'the initial language is',
                'i\'ve placed the module',
                'i\'m focusing on clarity'
              ];
              const shouldSkip = displayText.length < 15 || 
                                skipPhrases.some(phrase => displayText.toLowerCase().includes(phrase));
              
              if (displayText && !shouldSkip) {
                queueTeacherText(displayText, false, false, false);
              }
            } else if (msg.speaker === 'student' && msg.text) {
              setTranscript(prev => [...prev, { speaker: 'student', text: msg.text, timestamp: msg.timestamp }]);
            }
          });
        }
      }
    } catch (error) {
      addLog(`Poll error: ${error.message}`);
    }
  }, [sessionId, lastMessageIndex, getApiBaseUrl, addLog, queueTeacherText]);

  useEffect(() => {
    if (isConnected && sessionId) {
      pollIntervalRef.current = setInterval(pollTranscripts, 1000);
    } else if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, [isConnected, sessionId, pollTranscripts]);

  const setupTrackListeners = useCallback(() => {
    if (!rtviClientRef.current) return;
    rtviClientRef.current.on(RTVIEvent.TrackStarted, (track, participantInfo) => {
      if (!participantInfo?.local && track.kind === 'audio') setupAudioTrack(track);
    });
    rtviClientRef.current.on(RTVIEvent.UserTranscript, (data) => {
      setTranscript(prev => [...prev, { speaker: 'student', text: data.text, timestamp: new Date().toISOString() }]);
    });
    // Speaking events used for voice/text sync gating
    rtviClientRef.current.on(RTVIEvent.BotStartedSpeaking, () => {
      setIsSpeaking(true);
      isBotSpeakingRef.current = true;
      // Clear any pending cooldown since bot is speaking again
      if (botSpeakingCooldownRef.current) {
        clearTimeout(botSpeakingCooldownRef.current);
        botSpeakingCooldownRef.current = null;
      }
    });
    rtviClientRef.current.on(RTVIEvent.BotStoppedSpeaking, () => {
      // Brief cooldown to handle pauses between sentences
      botSpeakingCooldownRef.current = setTimeout(() => {
        isBotSpeakingRef.current = false;
        setIsSpeaking(false);
      }, 1000);
    });
  }, [setupAudioTrack]);

  const startPreview = async () => {
    try {
      setIsConnecting(true);
      setConnectionStatus('Connecting to preview...');
      const newSessionId = `preview_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 11)}`;
      setSessionId(newSessionId);
      audioContextRef.current = new AudioContext();
      await audioContextRef.current.resume();
      
      const params = new URLSearchParams({
        candidate_id: candidateId,
        candidate_name: candidateName,
        candidate_email: candidateEmail,
        language: 'en-US',
        duration: duration.toString(),
        voice: selectedVoice,
        session_id: newSessionId
      });
      
      addLog(`Selected voice: ${selectedVoice}`);
      addLog(`Full params: ${params.toString()}`);
      
      let baseUrl = getApiBaseUrl();
      const protocol = window.location.protocol;
      const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:';
      
      const connectEndpoint = `/api/course-teaching/ws/preview?${params.toString()}`;
      
      addLog(`Base URL: ${baseUrl}`);
      addLog(`Connect endpoint: ${connectEndpoint}`);
      
      const transport = new WebSocketTransport();
      const rtviConfig = {
        transport,
        params: {
          baseUrl: baseUrl,  // Use HTTP/HTTPS URL - transport will convert to WS/WSS
          endpoints: {
            connect: connectEndpoint,
          },
        },
        enableMic: false, // Start with mic disabled for preview
        enableCam: false,
        callbacks: {
          onConnected: () => {
            addLog('Connected to preview');
            setIsConnected(true);
            setIsConnecting(false);
            setConnectionStatus('Preview session active');
            setIsMicEnabled(false); // Start with mic disabled for preview
            
            // Setup audio track if available
            const tracks = rtviClientRef.current?.tracks();
            if (tracks?.bot?.audio) {
              setupAudioTrack(tracks.bot.audio);
            }
            
            timerIntervalRef.current = setInterval(() => {
              setTimeRemaining(prev => {
                if (prev <= 1) {
                  clearInterval(timerIntervalRef.current);
                  stopPreview();
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);
          },
          onDisconnected: () => {
            addLog('Disconnected from preview');
            setIsConnected(false);
            setConnectionStatus('Preview ended');
            setIsMicEnabled(false);
            if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
          },
          onBotReady: (data) => {
            addLog(`Bot ready: ${JSON.stringify(data)}`);
            const tracks = rtviClientRef.current?.tracks();
            if (tracks?.bot?.audio) {
              setupAudioTrack(tracks.bot.audio);
            }
          },
          onError: (error) => {
            addLog(`Error: ${error}`);
            setConnectionStatus('Error');
            setIsConnecting(false);
          },
        },
        timeout: 30000,
      };
      
      rtviClientRef.current = new RTVIClient(rtviConfig);
      
      setupTrackListeners();
      
      // Initialize devices
      addLog('Initializing audio devices...');
      await rtviClientRef.current.initDevices();
      
      // Connect to bot
      addLog('Connecting to bot...');
      await rtviClientRef.current.connect();
      
    } catch (error) {
      addLog(`Connection error: ${error.message}`);
      setConnectionStatus(`Error: ${error.message}`);
      setIsConnecting(false);
    }
  };

  const submitFeedback = async () => {
    if (feedbackRating === 0) {
      alert('Please select a rating');
      return;
    }
    
    try {
      const baseUrl = getApiBaseUrl();
      
      addLog(`Submitting feedback for session: ${sessionId}`);
      addLog(`Rating: ${feedbackRating}, Would enroll: ${wouldEnroll}`);
      
      const response = await fetch(`${baseUrl}/api/course-teaching/preview/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          rating: feedbackRating,
          feedback_text: feedbackText,
          would_enroll: wouldEnroll
        })
      });
      
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        addLog(`Feedback saved successfully: ${JSON.stringify(data)}`);
      } else {
        addLog(`Feedback submission failed (${response.status}): ${JSON.stringify(data)}`);
      }

      // Always proceed - show success to user regardless
      onPreviewEnd();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      addLog(`Feedback error: ${error.message}`);
      // Still close the feedback form
      onPreviewEnd();
    }
  };

  const handleVoiceChange = async (newVoice) => {
    setSelectedVoice(newVoice);
    
    // If already connected, we need to restart to apply the new voice
    if (isConnected) {
      addLog(`Voice changed to ${newVoice}, restarting session...`);
      setConnectionStatus('Applying new voice...');
      
      // Stop current session without showing feedback
      await stopPreview(false);
      
      // Wait a moment for cleanup
      setTimeout(() => {
        // Restart with new voice
        startPreview();
      }, 1000);
    }
  };

  const toggleMic = async () => {
    if (!rtviClientRef.current) return;
    try {
      if (isMicEnabled) {
        await rtviClientRef.current.enableMic(false);
      } else {
        await rtviClientRef.current.enableMic(true);
      }
      setIsMicEnabled(!isMicEnabled);
      addLog(`Mic ${!isMicEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // Fallback: toggle track directly
      try {
        const tracks = rtviClientRef.current.tracks();
        if (tracks?.local?.audio) {
          tracks.local.audio.enabled = !isMicEnabled;
          setIsMicEnabled(!isMicEnabled);
        }
      } catch (e) {
        addLog(`Mic toggle error: ${error.message}`);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (showFeedback) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4 z-50 overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full my-8">
          <div className="text-center mb-6">
            <Sparkles className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">How was your preview?</h2>
            <p className="text-gray-600">Help us improve your learning experience</p>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Rate your experience</label>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  onClick={() => setFeedbackRating(star)}
                  className="transition-transform hover:scale-110"
                >
                  <Star 
                    className={`w-10 h-10 ${star <= feedbackRating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                  />
                </button>
              ))}
            </div>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Tell us more (optional)</label>
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              rows="3"
              placeholder="What did you like? What could be better?"
            />
          </div>
          
          <div className="mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={wouldEnroll}
                onChange={(e) => setWouldEnroll(e.target.checked)}
                className="w-5 h-5 text-indigo-600 rounded focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">I'm interested in enrolling in a full course</span>
            </label>
          </div>
          
          <button
            onClick={submitFeedback}
            disabled={feedbackRating === 0}
            className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Submit Feedback
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col z-50">
      <audio ref={audioElementRef} autoPlay playsInline />
      
      {/* Header */}
      <div className="bg-white shadow-md px-6 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <Sparkles className="w-8 h-8 text-indigo-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Course Preview - Aptirix Live Teaching</h1>
            <p className="text-sm text-gray-600">
              {connectionStatus} • {isConnected ? 'Interactive AI Teacher ready' : 'Preview session'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-indigo-100 px-4 py-2 rounded-lg">
            <Clock className="w-5 h-5 text-indigo-600" />
            <span className="font-mono font-semibold text-indigo-900">{formatTime(timeRemaining)}</span>
          </div>
          
          {onExit && (
            <button onClick={onExit} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-6 h-6 text-gray-600" />
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Teaching Display */}
        <div className="flex-1 flex flex-col bg-white m-4 rounded-xl shadow-lg overflow-hidden">
          {/* View Tabs */}
          <div className="flex border-b border-gray-200 bg-gray-50">
            <button
              onClick={() => setActiveView('whiteboard')}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                activeView === 'whiteboard' ? 'bg-white text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Presentation className="w-5 h-5" />
              Whiteboard
            </button>
            <button
              onClick={() => setActiveView('code')}
              className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors ${
                activeView === 'code' ? 'bg-white text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Code className="w-5 h-5" />
              Code Editor
            </button>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-auto p-6">
            {activeView === 'whiteboard' && (
              <div ref={whiteboardRef} className="space-y-4">
                {whiteboardLines.map((line, idx) => (
                  <div key={idx} className="text-lg text-gray-800 font-mono whitespace-pre-wrap animate-fadeIn">
                    {line}
                  </div>
                ))}
                {diagrams.map((diagram, idx) => (
                  <div key={idx} className="border-2 border-indigo-300 rounded-lg p-4 bg-indigo-50 animate-fadeIn">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{diagram.description}</p>
                  </div>
                ))}
                {whiteboardLines.length === 0 && diagrams.length === 0 && (
                  <div className="text-center text-gray-400 mt-20">
                    <Presentation className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Whiteboard content will appear here</p>
                  </div>
                )}
              </div>
            )}

            {activeView === 'code' && (
              <div className="h-full">
                {codeContent ? (
                  <pre className="bg-gray-900 text-green-400 p-6 rounded-lg font-mono text-sm overflow-auto h-full">
                    {codeContent}
                  </pre>
                ) : (
                  <div className="text-center text-gray-400 mt-20">
                    <Code className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Code examples will appear here</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Teacher Speech Display */}
          {currentTeacherText && (
            <div className="bg-indigo-50 border-t-2 border-indigo-200 p-4">
              <div className="flex items-start gap-3">
                <div className={`w-3 h-3 rounded-full mt-1 ${isSpeaking ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                <p className="text-gray-800 flex-1">{currentTeacherText}</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Controls & Transcript */}
        <div className="w-96 bg-white m-4 ml-0 rounded-xl shadow-lg flex flex-col overflow-hidden">
          {/* Voice Selection */}
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Voice
              {isConnected && (
                <span className="text-xs text-orange-600 ml-2">(will restart session)</span>
              )}
            </label>
            <select
              value={selectedVoice}
              onChange={(e) => handleVoiceChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
              disabled={isConnecting}
            >
              {voiceOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {connectionStatus === 'Applying new voice...' && (
              <p className="text-xs text-orange-600 mt-1">Restarting with new voice...</p>
            )}
          </div>

          {/* Controls */}
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            {!isConnected ? (
              <button
                onClick={startPreview}
                disabled={isConnecting}
                className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-400 transition-colors flex items-center justify-center gap-2"
              >
                <Play className="w-5 h-5" />
                {isConnecting ? 'Connecting...' : 'Start Preview'}
              </button>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={toggleMic}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                    isMicEnabled ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-gray-600 hover:bg-gray-700 text-white'
                  }`}
                >
                  {isMicEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
                  {isMicEnabled ? 'Mute Mic' : 'Unmute Mic'}
                </button>
                
                <button
                  onClick={stopPreview}
                  className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
                >
                  End Preview
                </button>
                
                <p className="text-xs text-center text-gray-500 mt-2">
                  Ask questions anytime! The AI teacher will respond.
                </p>
              </div>
            )}
          </div>

          {/* Transcript */}
          <div className="flex-1 overflow-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Conversation</h3>
              <button
                onClick={() => setShowTranscript(!showTranscript)}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                {showTranscript ? 'Hide' : 'Show'}
              </button>
            </div>
            
            {showTranscript && (
              <div className="space-y-3">
                {transcript.map((msg, idx) => (
                  <div key={idx} className={`p-3 rounded-lg ${
                    msg.speaker === 'teacher' ? 'bg-indigo-50 border-l-4 border-indigo-500' : 'bg-gray-50 border-l-4 border-gray-400'
                  }`}>
                    <div className="text-xs font-semibold text-gray-600 mb-1">
                      {msg.speaker === 'teacher' ? '🤖 AI Teacher' : '👤 You'}
                    </div>
                    <div className="text-sm text-gray-800">{msg.text}</div>
                  </div>
                ))}
                {transcript.length === 0 && (
                  <div className="text-center text-gray-400 mt-8">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Conversation will appear here</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveCoursePreview;
